import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HiOutlineUserCircle } from "react-icons/hi2";
import { useLanguage } from "../../contexts/LanguageContext";
import LanguageSelector from "../common/LanguageSelector";

const logoUrl = process.env.PUBLIC_URL
  ? `${process.env.PUBLIC_URL}/logojes.png`
  : "/logojes.png";

/**
 * Layout pour les visiteurs (non connectés).
 * Header transparent sur le hero, se solidifie au scroll : la transition
 * signale visuellement le passage du hero plein cadre au reste du contenu.
 *
 * `variant="light"` adapte le header aux pages à fond clair (home "Terracotta
 * Studio") ; `variant="dark"` (défaut) préserve le header sombre historique
 * utilisé par les autres pages visiteur (recettes, communauté, etc.).
 */
const VisitorLayout = ({ children, variant = "dark" }) => {
  const { t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const isLight = variant === "light";

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className={`min-h-screen ${isLight ? "paper-field" : "ink-field"}`}>
      <header
        className={`${isLight ? "" : "dark"} sticky top-0 z-50 transition-[background-color,backdrop-filter,border-color,box-shadow] duration-300 ease-out border-b ${
          isScrolled
            ? isLight
              ? "bg-ink-50/90 backdrop-blur-xl border-ink-900/10 shadow-[0_10px_30px_rgba(0,0,0,0.06)]"
              : "bg-ink-900/85 backdrop-blur-xl border-white/10 shadow-[0_10px_30px_rgba(0,0,0,0.35)]"
            : "bg-transparent border-transparent shadow-none"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-[70px]">
            {/* Logo JesCook */}
            <Link
              to="/"
              className={`group flex items-center gap-2.5 sm:gap-3 transition-opacity hover:opacity-90 ${
                isLight ? "text-ink-900" : "text-white"
              }`}
              aria-label="JesCook accueil"
            >
              <div className="h-10 w-10 sm:h-11 sm:w-11 rounded-2xl bg-gradient-to-br from-ember-400 to-ember-600 p-0.5 shadow-lg shadow-ember-900/25">
                <div className="h-full w-full rounded-[14px] bg-ink-900/85 flex items-center justify-center">
                  <img
                    src={logoUrl}
                    alt="JesCook"
                    className="h-6 sm:h-7 w-auto object-contain"
                  />
                </div>
              </div>
              <div className="hidden sm:block">
                <span
                  className={`text-[1.15rem] font-display font-semibold tracking-[-0.01em] ${
                    isLight ? "text-ink-900" : "text-white"
                  }`}
                >
                  JesCook
                </span>
                <p
                  className={`text-[11px] uppercase tracking-[0.08em] font-semibold -mt-0.5 transition-colors ${
                    isLight
                      ? "text-ember-700/90 group-hover:text-ember-600"
                      : "text-ember-200/85 group-hover:text-ember-100"
                  }`}
                >
                  {t("worldMenus")}
                </p>
              </div>
            </Link>

            {/* Desktop: langue + boutons */}
            <div className="hidden md:flex items-center gap-2.5">
              <LanguageSelector />
              <Link
                to="/auth"
                className="inline-flex items-center justify-center gap-2 h-11 px-4 rounded-lg bg-ember-500 text-ink-950 hover:bg-ember-400 transition-colors duration-150 font-semibold text-sm"
                aria-label={t("login")}
              >
                <HiOutlineUserCircle className="w-5 h-5" />
                <span>{t("login")}</span>
              </Link>
            </div>

            {/* Mobile: langue + auth directs dans le header */}
            <div className="flex md:hidden items-center gap-2">
              <LanguageSelector compact />
              <Link
                to="/auth"
                className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-ember-500 text-ink-950 transition-transform duration-150 active:scale-95"
                aria-label={t("login")}
              >
                <HiOutlineUserCircle className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1">{children}</main>
    </div>
  );
};

export default VisitorLayout;
